/*
 * PROD-7828 R10 — pure shaping for the Reports tab: the status and table envelopes, cell text by
 * column kind, the file and sheet names, and the "Recalculate now" outcomes. No I/O (adapters.test.js).
 */
import { EM_DASH, TOTALS_ROUNDING_NOTE, fmt, fmt_count } from '../format';

// Mirrors of the server's reports.FileName rule and draft column (pinned to reports_enums.json in tests).
export const FILE_PREFIX = 'teacher_payments_';
export const DRAFT_SUFFIX = '_DRAFT';
export const ISSUES_FILE_PREFIX = 'teacher_payments_to_be_reviewed_';
export const REPORT_STATUS_COLUMN = 'report_status';
export const REPORT_STATUS_LABEL = 'Report status';

const list = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (typeof v === 'string' ? v : '');

const draft_view = (d) => ({
  is_draft: Boolean(d && d.is_draft),
  label: str(d && d.label)
});

export const blockers_view = (rows) =>
  list(rows).map((b) => ({
    teacher_uuid: str(b.teacher_uuid),
    teacher_name: str(b.teacher_name),
    lifecycle: str(b.lifecycle),
    needs_recalc: Boolean(b.needs_recalc),
    compute_error: str(b.compute_error)
  }));

export const status_view = (data) => {
  const d = data || {};
  return {
    pay_month: str(d.pay_month),
    month_label: str(d.month_label),
    draft: draft_view(d.draft),
    blocked: Boolean(d.blocked),
    blockers: blockers_view(d.blockers),
    reports: list(d.reports).map((r) => ({
      key: str(r.key),
      title: str(r.title),
      description: str(r.description)
    })),
    teacher_count: Number(d.teacher_count) || 0
  };
};

// OPEN -> "Open", NO_MONTH_ROW -> "No month row": the UI shows sentence case, never the enum.
export const lifecycle_label = (lifecycle) => {
  const s = str(lifecycle).replace(/_/g, ' ').toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

// Why a teacher blocks the month: the engine's error, else the stale flag ("D97").
export const blocker_reason = (b) =>
  b.compute_error || 'Marked for recalculation: the stored amounts are out of date.';

// ---- the report table (preview panel and XLSX) ----

export const table_view = (data) => {
  const d = data || {};
  return {
    report: str(d.report),
    title: str(d.title),
    pay_month: str(d.pay_month),
    draft: draft_view(d.draft),
    generated_at_ist: str(d.generated_at_ist),
    columns: list(d.columns).map((c) => ({
      key: str(c.key),
      label: str(c.label),
      kind: str(c.kind)
    })),
    rows: list(d.rows),
    total_rows: Number(d.total_rows) || 0,
    totals: d.totals && typeof d.totals === 'object' ? d.totals : {}
  };
};

const is_blank = (v) => v === null || v === undefined || v === '';

// What the preview shows in a cell; the files keep the raw JSON value.
export const cell_text = (kind, v) => {
  if (is_blank(v)) return EM_DASH;
  switch (kind) {
    case 'money2':
      return fmt(v, 2);
    case 'rupees':
      return fmt(v);
    case 'int':
      return fmt_count(v);
    case 'bool':
      return v ? 'Yes' : 'No';
    default:
      return String(v);
  }
};

// The additional-payments totals are not columns (D107/CD-112), so they carry their own names.
const TOTAL_LABELS = {
  net_total: 'Net (active entries)',
  removed_total: 'Removed by Ops',
  waived_total: 'Carry-forwards waived'
};

export const totals_view = (table) =>
  Object.keys(table.totals).map((key) => {
    const col = table.columns.find((c) => c.key === key);
    return {
      key,
      label: TOTAL_LABELS[key] || (col && col.label) || key,
      text: cell_text(col && col.kind === 'rupees' ? 'rupees' : 'money2', table.totals[key])
    };
  });

// ---- file and sheet names ----

// teacher_payments_<key>_<YYYY-MM>[_DRAFT].<ext>, the server's rule, for when its header is unreadable.
export const report_file_name = (key, pay_month, is_draft, ext) =>
  `${FILE_PREFIX}${key}_${pay_month}${is_draft ? DRAFT_SUFFIX : ''}${ext ? `.${ext}` : ''}`;

export const issues_file_name = (pay_month) =>
  `${ISSUES_FILE_PREFIX}${String(pay_month || '').slice(0, 7)}.csv`;

// Excel refuses a sheet name over 31 characters or holding : \ / ? * [ ] (the write throws).
const SHEET_NAME_MAX = 31;
export const sheet_name = (title, is_draft) =>
  `${is_draft ? 'DRAFT - ' : ''}${str(title)}`
    .replace(/[:\\/?*[\]]/g, ' ')
    .slice(0, SHEET_NAME_MAX)
    .trim();

// The XLSX is the full /reports/data JSON as a sheet: same columns and order, money as numbers, and on
// a draft month the report_status column first on every row ("D98", R10-C3). Text goes in unescaped: the
// library writes strings as text cells, which Excel never evaluates (CD-236).
export const xlsx_sheet = (table) => {
  const { is_draft, label } = table.draft;
  const columns = table.columns.map((c) => ({ label: c.label, value: c.key }));
  const out_columns = is_draft
    ? [{ label: REPORT_STATUS_LABEL, value: REPORT_STATUS_COLUMN }].concat(columns)
    : columns;
  const rows = is_draft
    ? table.rows.map((r) => ({ [REPORT_STATUS_COLUMN]: label, ...r }))
    : table.rows;
  // CD-237 addendum: matches the server CSV's final rounding-note row (D100).
  const note_row = { [out_columns[0].value]: TOTALS_ROUNDING_NOTE };
  return {
    file_name: report_file_name(table.report, table.pay_month, is_draft, ''),
    sheet_name: sheet_name(table.title, is_draft),
    columns: out_columns,
    rows: rows.concat([note_row])
  };
};

// ---- "Recalculate now" (CD-33) ----

export const RECALC_DONE = 'done';
export const RECALC_SKIPPED = 'skipped';
export const RECALC_FAILED = 'failed';

// The engine skips a month that is not OPEN (R2-M3) with developer text; Ops fix it by reopening.
const NOT_OPEN_RE = /month lifecycle is (\w+), not OPEN/;
const skip_text = (reason) => {
  const m = str(reason).match(NOT_OPEN_RE);
  if (!m) return 'Not recalculated.';
  const state = m[1] === 'CLOSED' ? 'closed' : 'not open';
  return `Not recalculated - this month is ${state}. Reopen it to recalculate.`;
};

export const recalc_outcome = (data) => {
  const r = (data && data.recompute) || {};
  if (str(r.error) || r.ok === false) {
    return {
      kind: RECALC_FAILED,
      text: `Failed: ${str(r.error) || 'the month could not be recalculated.'}`
    };
  }
  if (r.skipped) return { kind: RECALC_SKIPPED, text: skip_text(r.skip_reason) };
  return { kind: RECALC_DONE, text: 'Recalculated' };
};

export const recalc_failure = (message) => ({
  kind: RECALC_FAILED,
  text: `Failed: ${message || 'the recalculation request did not go through.'}`
});
